import React, { memo, useEffect, useState } from 'react'

import styled from 'styled-components'
import { useToasts } from 'react-toast-notifications'
import { useSelector, useDispatch } from 'react-redux'

import UpdateQuestionNotes from './components/UpdateQuestionNotes'

import Slider from './components/Slider'
import Question from './components/Question'

import DashboardButton from '../../DashboardButton'
import EmptyWrapper from '../../../EmptyWrapper'

import axiosInstance from '../../../../services/config/AxiosIntance'

import { fetchApplicantScorecard } from '../../../../store/actions/talentActions'

import { P } from '../../../../style/Typography.style'

const Assessments = ({ stage, interviewId }) => {
  const { addToast } = useToasts()
  const dispatch = useDispatch()

  const [jobQuestions, setJobQuestions] = useState([])
  const [answeredQuestions, setAnsweredQuestions] = useState([])
  const [selectedQuestion, setSelectedQuestion] = useState('')
  const [noteModal, setNoteModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [groupedQuestions, setGroupedQuestions] = useState([])

  const { selectedInterview } = useSelector((state) => state.requisitions)

  const handleSubmit = async () => {
    try {
      if (answeredQuestions.length <= 0)
        return addToast('Please provide your answers before submitting.', {
          appearance: 'error',
          autoDismiss: true,
        })

      const submittedQuestions = answeredQuestions.map((q) => ({
        ...q,
        answer: JSON.stringify({
          answer: q.answer || '1',
        }),
        guide_questions: q.guide_questions || '',
      }))

      const result = await axiosInstance.post(
        `/interview-ratings/save`,
        submittedQuestions
      )
      addToast(result?.data?.message, {
        appearance: 'success',
        autoDismiss: true,
      })
    } catch (error) {
      addToast('Something went wrong', {
        appearance: 'error',
        autoDismiss: true,
      })
    } finally {
      dispatch(fetchApplicantScorecard(selectedInterview?.applicant_id))
    }
  }

  const handleRangeChanged = (e) => {
    const questionId = e.target.dataset.questionid

    const payload = {
      interview_id: interviewId.toString(),
      question_id: questionId,
      answer: e.target.value,
    }

    const updatedAnswers = answeredQuestions.map((existingAnswer) => {
      if (existingAnswer.question_id === questionId) {
        return {
          ...existingAnswer,
          answer: e.target.value,
        }
      }
      return existingAnswer
    })

    if (!updatedAnswers.some((answer) => answer.question_id === questionId)) {
      updatedAnswers.push(payload)
    }

    setAnsweredQuestions(updatedAnswers)

    const updatedJobQuestions = jobQuestions.map((question) => {
      if (parseInt(question.question_id) === parseInt(questionId)) {
        return {
          ...question,
          answer: e.target.value,
        }
      } else {
        return question
      }
    })

    setJobQuestions(updatedJobQuestions)
  }

  const handleQuestionClicked = (questionId) => {
    const selected = answeredQuestions.filter(
      (q) => parseInt(q.question_id) === parseInt(questionId)
    )
    setSelectedQuestion(selected)
    setNoteModal(true)
  }

  useEffect(() => {
    let reqUrl = `/rating-questions/assessments?interview_id=${interviewId}&stage=${stage?.name || '1st Interview'
      }`

    if (stage?.name === '2nd Interview')
      reqUrl += `&job_id=${selectedInterview?.job_id}`

    axiosInstance
      .get(reqUrl)
      .then((res) => {
        setLoading(true)

        const response = res?.data?.resp

        const questions = []
        const defaultAnswers = []

        Object.keys(response).forEach((key) => {
          response[key].forEach((item) => {
            questions.push({
              assessment: key,
              interview_id: interviewId,
              applicant_id: selectedInterview?.applicant_id.toString(),
              question_id: item.question_id,
              question: item.question,
              answer: item.answer && JSON.parse(item.answer)?.answer,
              notes: item.notes,
              guide_questions: item.guide_questions,
              rating_matrix: item?.rating_matrix,
            })

            defaultAnswers.push({
              interview_id: interviewId.toString(),
              question_id: item.question_id.toString(),
              applicant_id: selectedInterview?.applicant_id.toString(),
              answer: item.answer && JSON.parse(item.answer)?.answer,
              notes: item?.notes || '',
              guide_questions: item?.guide_questions || '',
            })
          })
        })

        setJobQuestions(questions)
        setAnsweredQuestions(defaultAnswers)
      })
      .catch(() => {
        addToast('Something went wrong', {
          appearance: 'error',
          autoDismiss: true,
        })
      })
      .finally(() => setLoading(false))
  }, [interviewId, stage?.name])

  useEffect(() => {
    const grouped = jobQuestions.reduce((acc, question) => {
      const { assessment } = question
      if (!acc[assessment]) {
        acc[assessment] = []
      }
      acc[assessment].push(question)
      return acc
    }, {})

    setGroupedQuestions(grouped)
  }, [jobQuestions])

  const getRangeAnswer = (answer) => {
    if (answer === true || answer === false || answer === '') return 1
    return answer
  }

  const getMinMaxFromRatingMatrix = (ratings) => {
    const keys = Object.keys(ratings).map(Number)
    const min = Number(Math.min(...keys))
    const max = Number(Math.max(...keys))

    return { min, max }
  }

  return (
    <StyledRatingGroups>
      {loading && <EmptyWrapper message="Loading..." />}

      {groupedQuestions && Object.keys(groupedQuestions).length > 0 ? (
        Object.keys(groupedQuestions).map((assessment) => (
          <div key={assessment}>
            <P width="50%" weight="500">
              {assessment}
            </P>

            <ul>
              {groupedQuestions[assessment].map((question, i) => (
                <li key={question.question_id}>
                  <div className="questions">
                    <Question
                      jobQuestion={question}
                      onQuestionClicked={handleQuestionClicked}
                      note1={question.notes}
                      note2={answeredQuestions[i]?.notes}
                    />

                    <Slider
                      id={question.question_id}
                      value={getRangeAnswer(question?.answer)}
                      onChange={handleRangeChanged}
                      min={
                        getMinMaxFromRatingMatrix(question?.rating_matrix).min
                      }
                      max={
                        getMinMaxFromRatingMatrix(question?.rating_matrix).max
                      }
                      labels={question?.rating_matrix}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))
      ) : (
        <EmptyWrapper message="No questions available" />
      )}

      {['1st Interview', '2nd Interview'].includes(
        selectedInterview?.stageName
      ) && <DashboardButton text="Submit" onClick={handleSubmit} notIcon />}

      {noteModal && (
        <UpdateQuestionNotes
          answeredQuestions={answeredQuestions}
          setAnsweredQuestions={setAnsweredQuestions}
          currentQuestion={selectedQuestion}
          questionId={selectedQuestion}
          setNoteModal={setNoteModal}
        />
      )}
    </StyledRatingGroups>
  )
}

export default memo(Assessments)

const StyledRatingGroups = styled.div`
  height: auto;
  width: 100%;
  padding-bottom: 2rem;
  margin-bottom: 4rem;
  position: relative;
  list-style: none;

  ul {
    padding: 0;
    display: flex;
    flex-direction: column;
    margin-bottom: 0.5rem;
    overflow: auto;
  }

  li {
    .questions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 2rem;
    }
  }

  div {
    text-align: justify;
  }

  &::after {
    content: '';
    position: absolute;
  }

  &::before {
    content: '';
    position: absolute;
  }
`
